import { Badge } from '@shared/components/ui/badge';
import { useTranslation } from 'react-i18next';
import type {
  ProductOverlayLocationStock,
  ProductOverlayStockSummary,
} from './adapters/ProductOverlayStockApiAdapter';

export interface ProductStockPanelProps {
  summary: ProductOverlayStockSummary | null;
  locations: ProductOverlayLocationStock[];
  showLocations: boolean;
  isLoading: boolean;
  error: string | null;
}

export const ProductStockPanel = ({
  summary,
  locations,
  showLocations,
  isLoading,
  error,
}: ProductStockPanelProps) => {
  const { t } = useTranslation('ui');

  if (isLoading) {
    return (
      <div className="text-sm text-subtext py-2" data-testid="product-stock-loading">
        {t('product_overlay.loading_stock')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-error py-2" data-testid="product-stock-error">
        {t('product_overlay.stock_error')}
      </div>
    );
  }

  const disponible = summary?.disponible ?? 0;
  const reservado = summary?.reservado ?? 0;
  const total = disponible + reservado;
  const hasSummary = summary != null;
  const hasLocations = showLocations && locations.length > 0;

  if (!hasSummary && !hasLocations) {
    return (
      <div className="text-sm text-subtext py-2" data-testid="product-stock-empty">
        {t('product_overlay.stock_empty')}
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-3" data-testid="product-stock-panel">
      {hasSummary ? (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-card-bg rounded-md border border-lines py-2 flex flex-col items-center">
            <span className="text-xs text-subtext mb-0.5">{t('product_overlay.available')}</span>
            <span className="text-base font-medium text-text" data-testid="product-stock-disponible">
              {disponible}
            </span>
          </div>
          <div className="bg-card-bg rounded-md border border-lines py-2 flex flex-col items-center">
            <span className="text-xs text-subtext mb-0.5">{t('product_overlay.reserved')}</span>
            <span className="text-base font-medium text-text" data-testid="product-stock-reservado">
              {reservado}
            </span>
          </div>
          <div className="bg-card-bg rounded-md border border-lines py-2 flex flex-col items-center">
            <span className="text-xs text-subtext mb-0.5">{t('product_overlay.total')}</span>
            <span className="text-base font-medium text-text" data-testid="product-stock-total">
              {total}
            </span>
          </div>
        </div>
      ) : null}

      {showLocations ? (
        <div>
          <p className="text-xs font-medium text-subtext mb-2 uppercase tracking-wide">
            {t('product_overlay.locations_title')}
          </p>
          {locations.length === 0 ? (
            <p className="text-sm text-subtext" data-testid="product-stock-locations-empty">
              {t('product_overlay.locations_empty')}
            </p>
          ) : (
            <div className="w-full rounded-md overflow-hidden border border-card-border max-h-32 overflow-y-auto">
              <div className="grid grid-cols-12 text-xs border-b border-card-border py-1 px-2 bg-muted/30">
                <div className="col-span-5 font-medium text-subtext">{t('product_overlay.location_code')}</div>
                <div className="col-span-4 font-medium text-subtext">{t('product_overlay.location_type')}</div>
                <div className="col-span-3 font-medium text-subtext">{t('product_overlay.location_stock')}</div>
              </div>
              {locations.map((item) => (
                <div
                  key={item.id}
                  className="grid grid-cols-12 border-b border-card-border last:border-b-0 py-1.5 px-2 text-xs"
                >
                  <div className="col-span-5 truncate font-medium text-text">{item.code}</div>
                  <div className="col-span-4 min-w-0">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 max-w-full truncate">
                      {item.typeName || '—'}
                    </Badge>
                  </div>
                  <div className="col-span-3 font-medium text-text">{item.stock}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
